/**
 * Geography helpers — proxied through PearlCRM backend to avoid CountriesNow CORS.
 * Frontend caches responses in-memory so remounts/re-selects don't re-hit the API.
 */

const GROUPED_CITIES_TTL_MS = 24 * 60 * 60 * 1000;
const COUNTRIES_TTL_MS = 24 * 60 * 60 * 1000;
const groupedCitiesCache = new Map(); // countryKey -> { expiresAt, data }
const groupedCitiesInflight = new Map(); // countryKey -> Promise
let countriesCache = null; // { expiresAt, data }
let countriesInflight = null;

const countryCacheKey = (country) => String(country || "").trim().toLowerCase();

const isOtherOnlyGrouping = (items) =>
    Array.isArray(items)
    && items.length > 0
    && items.every((item) => (item?.state || 'Other') === 'Other');

const getCachedGroupedCities = (country) => {
    const key = countryCacheKey(country);
    const entry = groupedCitiesCache.get(key);
    if (!entry) return null;
    if (Date.now() > entry.expiresAt) {
        groupedCitiesCache.delete(key);
        return null;
    }
    if (!Array.isArray(entry.data) || entry.data.length === 0) {
        groupedCitiesCache.delete(key);
        return null;
    }
    if (isOtherOnlyGrouping(entry.data)) {
        groupedCitiesCache.delete(key);
        return null;
    }
    return entry.data;
};

/** Drop cached cities for one country (or all) — e.g. after a failed/empty proxy response. */
export const invalidateGroupedCitiesCache = (country = null) => {
    if (!country) {
        groupedCitiesCache.clear();
        groupedCitiesInflight.clear();
        return;
    }
    const key = countryCacheKey(country);
    groupedCitiesCache.delete(key);
    groupedCitiesInflight.delete(key);
};

const setCachedGroupedCities = (country, data) => {
    groupedCitiesCache.set(countryCacheKey(country), {
        expiresAt: Date.now() + GROUPED_CITIES_TTL_MS,
        data,
    });
};

export const fetchCitiesByCountry = async (axiosInstance, country) => {
    if (!country || !axiosInstance) return [];

    try {
        const grouped = await fetchGroupedCitiesByCountry(axiosInstance, country);
        return grouped.map((item) => item.city).filter(Boolean);
    } catch (error) {
        console.error(`Error fetching cities for ${country}:`, error);
        return [];
    }
};

export const fetchGroupedCitiesByCountry = async (
    axiosInstance,
    country,
    { forceRefresh = false } = {}
) => {
    if (!country || !axiosInstance) return [];

    if (forceRefresh) {
        invalidateGroupedCitiesCache(country);
    }

    const cached = getCachedGroupedCities(country);
    if (cached) return cached;

    const key = countryCacheKey(country);
    if (groupedCitiesInflight.has(key)) {
        return groupedCitiesInflight.get(key);
    }

    const requestPromise = (async () => {
        try {
            const response = await axiosInstance.post('/geography/grouped-cities', {
                country: String(country).trim(),
            });
            const data = Array.isArray(response.data) ? response.data : [];
            if (data.length > 0 && !isOtherOnlyGrouping(data)) {
                setCachedGroupedCities(country, data);
            } else {
                invalidateGroupedCitiesCache(country);
            }
            return data;
        } catch (error) {
            console.error(`Error fetching grouped cities for ${country}:`, error);
            invalidateGroupedCitiesCache(country);
            return [];
        } finally {
            groupedCitiesInflight.delete(key);
        }
    })();

    groupedCitiesInflight.set(key, requestPromise);
    return requestPromise;
};

/** Load several countries without firing all requests at once. */
export const fetchGroupedCitiesForCountries = async (
    axiosInstance,
    countries,
    { concurrency = 1, forceRefresh = false } = {}
) => {
    const uniqueCountries = [...new Set((countries || []).filter(Boolean))];
    const results = [];
    let index = 0;

    const worker = async () => {
        while (index < uniqueCountries.length) {
            const currentIndex = index;
            index += 1;
            const country = uniqueCountries[currentIndex];
            results[currentIndex] = await fetchGroupedCitiesByCountry(
                axiosInstance,
                country,
                { forceRefresh }
            );
        }
    };

    const workers = Array.from(
        { length: Math.min(concurrency, uniqueCountries.length || 1) },
        () => worker()
    );
    await Promise.all(workers);
    return results;
};

export const fetchStatesByCountry = async (
    axiosInstance,
    country,
    { includeHidden = false } = {}
) => {
    if (!country || !axiosInstance) return [];

    try {
        const res = await axiosInstance.get('/geography/states', {
            params: {
                country: String(country).trim(),
                ...(includeHidden ? { include_hidden: true } : {}),
            },
        });
        if (Array.isArray(res.data) && res.data.length > 0) {
            return [...res.data].sort((a, b) => String(a).localeCompare(String(b)));
        }
    } catch (error) {
        console.error(`Error fetching states from geography master for ${country}:`, error);
    }

    try {
        const grouped = await fetchGroupedCitiesByCountry(axiosInstance, country);
        return [...new Set(grouped.map((item) => item.state).filter(Boolean))].sort((a, b) =>
            a.localeCompare(b)
        );
    } catch (error) {
        console.error(`Error fetching states for ${country}:`, error);
        return [];
    }
};

export const fetchCitiesByCountryAndState = async (axiosInstance, country, state) => {
    if (!country || !axiosInstance) return [];

    try {
        const res = await axiosInstance.get('/geography/cities', {
            params: {
                country: String(country).trim(),
                ...(state ? { state: String(state).trim() } : {}),
            },
        });
        if (Array.isArray(res.data) && res.data.length > 0) {
            return [...res.data].sort((a, b) => String(a).localeCompare(String(b)));
        }
    } catch (error) {
        console.error(`Error fetching cities from geography master for ${country}/${state}:`, error);
    }

    try {
        const grouped = await fetchGroupedCitiesByCountry(axiosInstance, country);
        const filtered = state
            ? grouped.filter((item) => (item.state || "").toLowerCase() === String(state).toLowerCase())
            : grouped;
        return [...new Set(filtered.map((item) => item.city).filter(Boolean))].sort((a, b) =>
            a.localeCompare(b)
        );
    } catch (error) {
        console.error(`Error fetching cities for ${country}/${state}:`, error);
        return [];
    }
};

let currenciesCache = null;
let currenciesInflight = null;

export const fetchCurrencies = async (axiosInstance) => {
    if (currenciesCache && Date.now() <= currenciesCache.expiresAt) {
        return currenciesCache.data;
    }
    if (currenciesInflight) {
        return currenciesInflight;
    }

    currenciesInflight = (async () => {
        try {
            if (axiosInstance) {
                const res = await axiosInstance.get('/geography/currencies');
                const list = Array.isArray(res.data) ? res.data : [];
                if (list.length > 0) {
                    const normalized = list
                        .map((c) => ({
                            code: String(c.code || '').trim().toUpperCase(),
                            name: String(c.name || c.code || '').trim(),
                            symbol: String(c.symbol || c.code || '').trim(),
                            label: c.label || (c.name ? `${c.code} (${c.name})` : c.code),
                        }))
                        .filter((c) => c.code)
                        .sort((a, b) => a.code.localeCompare(b.code));
                    currenciesCache = {
                        expiresAt: Date.now() + COUNTRIES_TTL_MS,
                        data: normalized,
                    };
                    return normalized;
                }
            }
            return [];
        } catch (error) {
            console.error('Error fetching currencies from geography master:', error);
            return [];
        } finally {
            currenciesInflight = null;
        }
    })();

    return currenciesInflight;
};

export const invalidateCountriesCache = () => {
    countriesCache = null;
    countriesInflight = null;
    // Also drop city/state groupings so renamed/hidden geography shows up immediately.
    invalidateGroupedCitiesCache();
};

export const fetchAllCountries = async (
    axiosInstance,
    { includeHidden = false } = {}
) => {
    // includeHidden must not reuse the public 24h cache (hidden countries omitted there).
    if (!includeHidden) {
        if (countriesCache && Date.now() <= countriesCache.expiresAt) {
            return countriesCache.data;
        }
        if (countriesInflight) {
            return countriesInflight;
        }
    }

    const loadCountries = async () => {
        try {
            // Prefer public geography master tables
            if (axiosInstance) {
                try {
                    const res = await axiosInstance.get('/geography/countries', {
                        params: includeHidden ? { include_hidden: true } : undefined,
                    });
                    const countries = Array.isArray(res.data) ? res.data : [];
                    if (countries.length > 0) {
                        const normalized = countries
                            .map((c) => ({
                                code: String(c.code || c.iso2 || '').trim(),
                                label: String(c.label || c.name || '').trim(),
                                currency_code: c.currency_code || '',
                            }))
                            .filter((country) => country.label)
                            .sort((a, b) => a.label.localeCompare(b.label));
                        if (!includeHidden) {
                            countriesCache = {
                                expiresAt: Date.now() + COUNTRIES_TTL_MS,
                                data: normalized,
                            };
                        }
                        return normalized;
                    }
                } catch (error) {
                    console.error('Error fetching countries from geography master:', error);
                }

                if (includeHidden) {
                    return [];
                }

                try {
                    const res = await axiosInstance.get('/dropdown-data');
                    const countries = res?.data?.dropdowns?.countries;
                    if (Array.isArray(countries) && countries.length > 0) {
                        const normalized = countries
                            .map((name) => ({ code: "", label: String(name || "").trim() }))
                            .filter((country) => country.label)
                            .sort((a, b) => a.label.localeCompare(b.label));
                        countriesCache = {
                            expiresAt: Date.now() + COUNTRIES_TTL_MS,
                            data: normalized,
                        };
                        return normalized;
                    }
                } catch (error) {
                    console.error('Error fetching countries from dropdown-data:', error);
                }
            }

            if (includeHidden) {
                return [];
            }

            // Last-resort external fallback
            const response = await fetch('https://restcountries.com/v3.1/all?fields=cca2,name');
            if (!response.ok) return [];
            const data = await response.json();
            const normalized = (Array.isArray(data) ? data : [])
                .map((country) => ({
                    code: country?.cca2 || '',
                    label: country?.name?.common || '',
                }))
                .filter((country) => country.label)
                .sort((a, b) => a.label.localeCompare(b.label));
            if (normalized.length > 0) {
                countriesCache = {
                    expiresAt: Date.now() + COUNTRIES_TTL_MS,
                    data: normalized,
                };
            }
            return normalized;
        } catch (error) {
            console.error('Error fetching countries:', error);
            return [];
        }
    };

    if (includeHidden) {
        return loadCountries();
    }

    countriesInflight = (async () => {
        try {
            return await loadCountries();
        } finally {
            countriesInflight = null;
        }
    })();

    return countriesInflight;
};
